import cytoscape from "cytoscape";
import { TrailblazerEdgeType, TrailblazerNodeType } from "../data/TrailblazerTypes";

export default class TrailblazerStyleConfig {
    /**
     * Style for a BaseTrailblazerNode.
     */
    static BASE_NODE_STYLE = {
        "label": "data(label)",
        "text-valign": "center",
        "text-halign": "center",
        "text-justification": "left",
        "background-color": "#ADD8E6",
        "color": "#000",
        "shape": "round-rectangle",
        "padding": "15px",
        "width": "label",
        "height": "label",
        "text-wrap": "wrap",
        "text-max-width": "150px",
        "corner-radius": "8px"
    };

    /**
     * Style for CollapsedTrailblazerNode.
     */
    static COLLAPSED_NODE_STYLE = {
        "label": "data(label)",
        "text-valign": "center",
        "background-color": "#90EE90",
        "color": "#000",
        "text-wrap": "wrap",
        "text-max-width": "100px",
        "shape": "ellipse",
        "padding": "5px"
    };

    /**
     * Style for a PHYSICAL TrailblazerEdge.
     */
    static PHYSICAL_EDGE_STYLE = {
        "width": 2,
        "line-color": "#000",
        "line-style": "solid",
        "curve-style": "straight",
        "target-arrow-shape": "none",
        "source-arrow-shape": "none",
        "label": ""
    };

    /**
     * Style for a LOGICAL TrailblazerEdge.
     */
    static LOGICAL_EDGE_STYLE = {
        "width": 2,
        "line-color": "#7A7A7A",
        "line-style": "dashed",
        "curve-style": "unbundled-bezier",
        "control-point-distances": [150],
        "control-point-weights": [0.5],
        "label": "data(label)"
    };

    static DISPLAY_COLOR_NAMES = {
        "Passive To Passive": "#E8F4FD",
        "Mux To Mux": "#D1ECF1",
        "Trunk To Trunk": "#C8E6C9",
        "Client To Client": "#B3D9FF",
        "Router To DWDM": "#B8D8D8",
        "Encryption To Encryption": "#D4C5F9",
        "Encryption To DWDM": "#C8D4F7",
        "Router To Encryption": "#B8C8F5",
        "Router To Router": "#C1D3F0"
    };

    /**
     * Base stylesheet for Trailblazer Cytoscape visualization.
     */
    static get BASE_STYLESHEET() {
        return cytoscape.stylesheet()
            .selector(`edge[edgeType="${TrailblazerEdgeType.DIRECT}"]`)
            .style(this.PHYSICAL_EDGE_STYLE)
            .selector(`edge[edgeType="${TrailblazerEdgeType.LOGICAL}"]`)
            .style(this.LOGICAL_EDGE_STYLE)
            .selector(`node[nodeType="${TrailblazerNodeType.BASE}"]`)
            .style(this.BASE_NODE_STYLE)
            .selector(`node[nodeType="${TrailblazerNodeType.COLLAPSED}"]`)
            .style(this.COLLAPSED_NODE_STYLE);
    }

    /**
     * Generate complete stylesheet with dynamic linkType selectors
     */
    static generateStylesheet() {
        let stylesheet = this.BASE_STYLESHEET;

        // Add selectors for each link type from DISPLAY_COLOR_NAMES
        Object.entries(this.DISPLAY_COLOR_NAMES).forEach(([linkType, color]) => {
            stylesheet = stylesheet
                .selector(`node[linkType="${linkType}"]`)
                .style({
                    ...this.BASE_NODE_STYLE,
                    "background-color": color
                });
        });

        return stylesheet;
    }

    /**
     * Options for Trailblazer Cytoscape visualization.
     */
    static CYTOSCAPE_OPTIONS = {
        // interaction options:
        minZoom: 0.2,
        maxZoom: 1,
        zoomingEnabled: true,
        userZoomingEnabled: true,
        panningEnabled: true,
        userPanningEnabled: true,
        boxSelectionEnabled: false,
        selectionType: "single",
        touchTapThreshold: 8,
        desktopTapThreshold: 1,
        autolock: false,
        autoungrabify: false,
        autounselectify: false,

        // rendering options:
        headless: false,
        styleEnabled: true,
        hideEdgesOnViewport: false,
        textureOnViewport: false,
        motionBlur: false,
        motionBlurOpacity: 0.2,
        pixelRatio: "auto"
    };

    /**
     * Option for dagre layout that creates left-to-right layout.
     */
    static DAGRE_LAYOUT_OPTIONS = {
        name: "dagre",
        rankDir: "LR",
        ranker: "network-simplex",
        nodeSep: 0,
        edgeSep: 50,
        rankSep: 50,
        padding: 30,
        edgeWeight: edge =>
            // Prioritize physical connections (weight=1) over logical ones (weight=0.001)
            (edge.data("type") === TrailblazerEdgeType.DIRECT ? 1 : 0.001)
    };
}